import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import emailjs from "@emailjs/browser";
import { HiOutlineMail, HiOutlinePhone, HiOutlineLocationMarker } from "react-icons/hi";
import { PageHeader } from "../components/ui";

const DESCRIPTION =
  "Discuss a semiconductor engineering milestone, SOW team, specialist requirement or BOT program with Chiparama's senior team.";

export default function ContactPage() {
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [smsConsent, setSmsConsent] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    emailjs
      .sendForm("service_58jl8rh", "template_lnkxw49", form.current, "WZ8UBSqUYOkPtkkuy")
      .then(
        () => {
          setStatus({ ok: true, text: "Thank you. A senior Chiparama lead will review your message and respond." });
          form.current.reset();
          setSmsConsent(false);
          setLoading(false);
        },
        () => {
          setStatus({ ok: false, text: "We couldn't send your message. Please try again or email contact@chiparama.com." });
          setLoading(false);
        }
      );
  };

  return (
    <>
      <Helmet>
        <title>Contact Chiparama | Get in Touch With Our Team</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://chiparama.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Chiparama | Get in Touch With Our Team" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content="https://chiparama.com/contact" />
        <meta property="og:image" content="https://chiparama.com/chiparamalogo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Chiparama | Get in Touch With Our Team" />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://chiparama.com/chiparamalogo.jpg" />
      </Helmet>

      <PageHeader eyebrow="Contact" title="Get in touch.">
        Have a question or a program in mind? Fill out the form and our team will get back to you
        shortly. Building a startup team instead?{" "}
        <Link to="/startup-workforce" className="text-brand-light underline underline-offset-4 hover:text-white">
          Visit Startup Workforce
        </Link>
        .
      </PageHeader>

      <section className="section bg-paper">
        <div className="container-x grid items-start gap-10 lg:grid-cols-[1.4fr_1fr]">
          <form ref={form} onSubmit={sendEmail} className="card !p-7 md:!p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="user_name" className="field-label">Your name</label>
                <input type="text" id="user_name" name="user_name" className="field" placeholder="Jane Smith" autoComplete="name" required />
              </div>
              <div>
                <label htmlFor="user_email" className="field-label">Your email</label>
                <input type="email" id="user_email" name="user_email" className="field" placeholder="you@company.com" autoComplete="email" required />
              </div>
              <div>
                <label htmlFor="user_phone" className="field-label">
                  Phone number <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <input type="tel" id="user_phone" name="user_phone" className="field" placeholder="(555) 555-5555" autoComplete="tel" />
              </div>
              <div>
                <label htmlFor="subject" className="field-label">Subject</label>
                <input type="text" id="subject" name="subject" className="field" placeholder="What is this regarding?" required />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="field-label">Your message</label>
              <textarea
                id="message"
                name="message"
                rows="6"
                className="field"
                placeholder="Domain, stage and delivery need. Please do not include confidential or export-controlled information."
                required
              />
            </div>

            {/* SMS opt-in: optional, unchecked by default, does not block submission */}
            <div className="mt-6 flex items-start gap-3">
              <input
                type="checkbox"
                id="sms_opt_in"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand focus:ring-brand"
              />
              <label htmlFor="sms_opt_in" className="text-xs leading-relaxed text-slate-500">
                By opting in for text messages, you agree to receive job opening alerts and company
                updates from Chiparama Inc at the number provided. Message frequency varies. Msg &amp;
                data rates may apply. Reply STOP to unsubscribe. Reply HELP for help. View our{" "}
                <Link to="/privacy-policy" className="link">Privacy Policy</Link> and{" "}
                <Link to="/terms-and-conditions" className="link">Terms &amp; Conditions</Link> for more
                information.
                <span className="mt-1 block">
                  Consent to receive SMS messages is not a condition of purchasing any goods or services.
                </span>
              </label>
            </div>
            <input type="hidden" name="sms_consent" value={smsConsent ? "Yes" : "No"} />

            <button type="submit" disabled={loading} className="btn-primary mt-8 w-full py-3.5">
              {loading ? "Sending..." : "Send message"}
            </button>
            {status && (
              <p role="status" className={`mt-4 text-sm ${status.ok ? "text-emerald-700" : "text-red-600"}`}>
                {status.text}
              </p>
            )}
          </form>

          <aside className="on-dark rounded-2xl bg-navy-900 p-8 text-white md:p-10">
            <h2 className="text-xl font-semibold">Chiparama Inc</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              US program leadership with engineering depth across India.
            </p>
            <ul className="mt-8 space-y-6 text-sm">
              <li className="flex gap-4">
                <HiOutlineLocationMarker className="mt-0.5 h-5 w-5 shrink-0 text-brand-light" aria-hidden="true" />
                <span className="text-slate-300">10605 Jonwood Way, Austin, TX 78753, USA</span>
              </li>
              <li className="flex gap-4">
                <HiOutlinePhone className="mt-0.5 h-5 w-5 shrink-0 text-brand-light" aria-hidden="true" />
                <a href="tel:+14085948864" className="text-slate-300 hover:text-white">+1 (408) 594-8864</a>
              </li>
              <li className="flex gap-4">
                <HiOutlineMail className="mt-0.5 h-5 w-5 shrink-0 text-brand-light" aria-hidden="true" />
                <a href="mailto:contact@chiparama.com" className="text-slate-300 hover:text-white">contact@chiparama.com</a>
              </li>
            </ul>
            <p className="mt-10 border-t border-white/10 pt-6 text-xs leading-relaxed text-slate-400">
              Looking for a role? Visit the <Link to="/candidates" className="text-brand-light underline underline-offset-4">Candidate Hub</Link>.
              Candidates should not use this form.
            </p>
          </aside>
        </div>
      </section>
    </>
  );
}
